import { Injectable } from '@angular/core';

export enum MediaType {
  VIDEO = 'video',
  IMAGE = 'image'
}

interface StoredMedia {
  id?: number;
  name: string;
  mediaContent: ArrayBuffer;
  contentType: string;
  url: string;
  timestamp: number;
  size: number;
  type: MediaType;
}

interface StorageQuotaConfig {
  maxTotalSizeBytes: number;
  maxIndividualSizeBytes: {
    [key in MediaType]: number;
  };
  maxMediaCount: {
    [key in MediaType]: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class MultimediaStorageService {
  private dbName = 'MultimediaStorageDB';
  private storeName = 'media';
  private db: IDBDatabase | null = null;

  private quotaConfig: StorageQuotaConfig = {
    maxTotalSizeBytes: 1024 * 1024 * 1024, // 1GB total
    maxIndividualSizeBytes: {
      [MediaType.VIDEO]: 200 * 1024 * 1024, // 200 MB per video
      [MediaType.IMAGE]: 10 * 1024 * 1024   // 10 MB per image
    },
    maxMediaCount: {
      [MediaType.VIDEO]: 30, // Max 30 videos
      [MediaType.IMAGE]: 500  // Max 200 images
    }
  };

  constructor() {
    this.initDatabase().then(console.log).catch(console.error);
  }

  private initDatabase(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 4);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, {
            keyPath: 'id',
            autoIncrement: true
          });

          store.createIndex('url', 'url', { unique: false });
        }
      };

      request.onsuccess = (event) => {
        this.db = (event.target as IDBOpenDBRequest).result;
        console.log('IndexedDB success', this.db);
        resolve(this.db);
      };

      request.onerror = (event) => {
        console.error('IndexedDB error:', event);
        reject(event);
      };
    });
  }

  setStorageQuota(config: Partial<StorageQuotaConfig>) {
    this.quotaConfig = { ...this.quotaConfig, ...config };
  }


  getStoredMediaByUrl(url: string, mediaType: MediaType): Promise<StoredMedia> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);

      const index = store.index('url');
      const query = IDBKeyRange.only(url);

      const request = index.get(query);

      request.onsuccess = async () => {
        if (request.error) {
          reject(request.error);
        }

        if (!request.result) {
          const media = await this.saveMediaFromUrl(url, mediaType);
          resolve(media);
        } else {
          resolve(request.result);
        }
      };

      request.onerror = (event) => {
        reject(event);
      };
    });
  }

  async saveMediaFromUrl(url: string, mediaType: MediaType, name?: string): Promise<StoredMedia> {
    try {
      const isImage = mediaType === MediaType.IMAGE;
      const mediaTypeConfig = isImage ? MediaType.IMAGE : MediaType.VIDEO;

      const existingMedia = await this.listMediaByType(mediaTypeConfig);
      if (existingMedia.length >= this.quotaConfig.maxMediaCount[mediaTypeConfig]) {
        throw new Error(`Storage limit reached: Maximum number of ${mediaTypeConfig}s`);
      }

      const response = await fetch(url, { mode: 'cors' });
      const contentType = response.headers.get('Content-Type') ||
        (isImage ? 'image/jpeg' : 'video/mp4');
      const mediaContent = await response.arrayBuffer();

      const maxSize = this.quotaConfig.maxIndividualSizeBytes[mediaTypeConfig];
      if (mediaContent.byteLength > maxSize) {
        throw new Error(`${mediaTypeConfig} exceeds maximum size limit of ${this.formatBytes(maxSize)}`);
      }

      const currentTotalSize = await this.getStorageUsed();
      if (currentTotalSize + mediaContent.byteLength > this.quotaConfig.maxTotalSizeBytes) {
        await this.freeUpSpace(mediaContent.byteLength, mediaTypeConfig);
      }

      const mediaToStore: StoredMedia = {
        name: name || this.extractFilenameFromUrl(url),
        mediaContent: mediaContent,
        contentType: contentType,
        url: url,
        timestamp: Date.now(),
        size: mediaContent.byteLength,
        type: mediaTypeConfig
      };


      return new Promise((resolve, reject) => {
        if (!this.db) {
          reject(new Error('Database not initialized'));
          return;
        }

        const transaction = this.db.transaction([this.storeName], 'readwrite');
        const store = transaction.objectStore(this.storeName);
        const request = store.add(mediaToStore);

        request.onsuccess = () => {
          mediaToStore.id = request.result as number;
          resolve(mediaToStore);
        };

        request.onerror = (event) => {
          reject(event);
        };
      });
    } catch (error) {
      console.error('Error saving media:', error);
      throw error;
    }
  }

  private async freeUpSpace(requiredSpace: number, mediaType: MediaType): Promise<void> {
    const allMedia = await this.listMediaByType(mediaType);
    const sortedMedia = allMedia.sort((a, b) => a.timestamp - b.timestamp);

    let freedSpace = 0;
    for (const media of sortedMedia) {
      if (freedSpace >= requiredSpace) break;

      await this.deleteMedia(media.id!);
      freedSpace += media.size;
    }

    if (freedSpace < requiredSpace) {
      throw new Error('Cannot free enough storage space');
    }
  }

  async listMediaByType(mediaType: MediaType): Promise<StoredMedia[]> {
    const allMedia = await this.listMedia();
    return allMedia.filter(media => media.type === mediaType);
  }

  async getStorageInfo(): Promise<{
    totalUsed: number;
    mediaCount: { [key in MediaType]: number };
    maxTotalSize: number;
    maxMediaCount: { [key in MediaType]: number };
    availableSpace: number;
    percentageUsed: number;
  }> {
    const allMedia = await this.listMedia();
    const totalUsed = await this.getStorageUsed();

    const mediaCount = allMedia.reduce((acc, media) => {
      acc[media.type] = (acc[media.type] || 0) + 1;
      return acc;
    }, {} as { [key in MediaType]: number });

    return {
      totalUsed,
      mediaCount,
      maxTotalSize: this.quotaConfig.maxTotalSizeBytes,
      maxMediaCount: this.quotaConfig.maxMediaCount,
      availableSpace: this.quotaConfig.maxTotalSizeBytes - totalUsed,
      percentageUsed: (totalUsed / this.quotaConfig.maxTotalSizeBytes) * 100
    };
  }

  createMediaUrl(media: StoredMedia): string {
    return URL.createObjectURL(new Blob([media.mediaContent], { type: media.contentType }));
  }

  async listMedia(): Promise<StoredMedia[]> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.getAll();

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onerror = (event) => {
        reject(event);
      };
    });
  }

  async deleteMedia(id: number): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(id);

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = (event) => {
        reject(event);
      };
    });
  }

  async getStorageUsed(): Promise<number> {
    const media = await this.listMedia();
    return media.reduce((total, item) => total + item.size, 0);
  }

  private extractFilenameFromUrl(url: string): string {
    return new URL(url).pathname.split('/').pop() || `media_${Date.now()}`;
  }

  private formatBytes(bytes: number, decimals = 2): string {
    if (bytes === 0) {
      return '0 Bytes';
    }

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }
}